// Copyright (C) 2022-2024 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import { fabric } from 'fabric';
import debounce from 'lodash/debounce';

import {
    DrawData, MasksEditData, Geometry, Configuration, BrushTool, ColorBy,
} from './canvasModel';
import consts from './consts';
import { DrawHandler } from './drawHandler';
import {
    PropType, computeWrappingBox, zipChannels, expandChannels //, imageDataToDataURL,
} from './shared';
import { isEmpty } from 'lodash';

interface WrappingBBox {
    left: number;
    top: number;
    right: number;
    bottom: number;
}

interface CroppedImageData {
    imageData: Uint8ClampedArray;
    bbox: WrappingBBox;
  }
export interface MasksHandler {
    draw(drawData: DrawData): void;
    edit(state: MasksEditData): void;
    configurate(configuration: Configuration): void;
    transform(geometry: Geometry): void;
    updateThermalData(thermalData: any, refArray:Int16Array): void;
    cancel(): void;
    enabled: boolean;
}

export class MasksHandlerImpl implements MasksHandler {
    private onDrawDone: (
        data: object | null,
        duration?: number,
        continueDraw?: boolean,
        prevDrawData?: DrawData,
    ) => void;
    private onDrawRepeat: (data: DrawData) => void;
    private onEditStart: (state: any) => void;
    private onEditDone: (state: any, points: number[]) => void;
    private vectorDrawHandler: DrawHandler;

    private redraw: number | null;
    private isDrawing: boolean;
    private isEditing: boolean;
    private isInsertion: boolean;
    private isMouseDown: boolean;
    private isBrushSizeChanging: boolean;
    private resizeBrushToolLatestX: number;
    private brushMarker: fabric.Rect | fabric.Circle | null;
    private drawablePolygon: null | fabric.Polygon;
    private isPolygonDrawing: boolean;
    private drawnObjects: (fabric.Polygon | fabric.Circle | fabric.Rect | fabric.Line | fabric.Image)[];

    private tool: DrawData['brushTool'] | null;
    private drawData: DrawData | null;
    private canvas: fabric.Canvas;
    private canvasScale = 4;

    private editData: MasksEditData | null;

    private colorBy: ColorBy;
    private latestMousePos: { x: number; y: number; };
    private startTimestamp: number;
    private geometry: Geometry;
    private drawingOpacity: number;

    private thermalData: any | null;
    private oneshotMask: fabric.Image;

    private keepDrawnPolygon(): void {
        const canvasWrapper = this.canvas.getElement().parentElement;
        canvasWrapper.style.pointerEvents = '';
        canvasWrapper.style.zIndex = '';
        this.isPolygonDrawing = false;
        this.vectorDrawHandler.draw({ enabled: false }, this.geometry);
    }

    private removeBrushMarker(): void {
        if (this.brushMarker) {
            this.canvas.remove(this.brushMarker);
            this.brushMarker = null;
            this.canvas.renderAll();
        }
    }

    private setupBrushMarker(): void {
        if (['brush', 'eraser'].includes(this.tool.type)) {
            const common = {
                evented: false,
                selectable: false,
                opacity: 0.75,
                left: this.latestMousePos.x - this.tool.size * this.canvasScale / 2,
                top: this.latestMousePos.y - this.tool.size  * this.canvasScale / 2,
                stroke: 'white',
                strokeWidth: 1,
            };
            this.brushMarker = this.tool.form === 'circle' ? new fabric.Circle({
                ...common,
                radius: this.tool.size * this.canvasScale / 2,
            }) : new fabric.Rect({
                ...common,
                width: this.tool.size * this.canvasScale,
                height: this.tool.size * this.canvasScale,
            });

            this.canvas.defaultCursor = 'none';
            this.canvas.add(this.brushMarker);
        } else {
            this.canvas.defaultCursor = 'inherit';
        }
    }

    private releaseCanvasWrapperCSS(): void {
        const canvasWrapper = this.canvas.getElement().parentElement;
        canvasWrapper.style.pointerEvents = '';
        canvasWrapper.style.zIndex = '';
        canvasWrapper.style.display = '';
    }

    private releasePaste(): void {
        this.releaseCanvasWrapperCSS();
        this.canvas.clear();
        this.canvas.renderAll();
        this.isInsertion = false;
        this.drawnObjects = this.createDrawnObjectsArray();
        this.onDrawDone(null);
    }

    private releaseDraw(): void {
        this.removeBrushMarker();
        this.releaseCanvasWrapperCSS();
        if (this.isPolygonDrawing) {
            this.isPolygonDrawing = false;
            this.vectorDrawHandler.cancel();
        }
        this.canvas.clear();
        this.canvas.renderAll();
        this.isDrawing = false;
        this.isInsertion = false;
        this.redraw = null;
        this.drawnObjects = this.createDrawnObjectsArray();
    }

    private releaseEdit(): void {
        this.removeBrushMarker();
        this.releaseCanvasWrapperCSS();
        if (this.isPolygonDrawing) {
            this.isPolygonDrawing = false;
            this.vectorDrawHandler.cancel();
        }
        this.canvas.clear();
        this.canvas.renderAll();
        this.isEditing = false;
        this.drawnObjects = this.createDrawnObjectsArray();
        this.onEditDone(null, null);
    }

    private getStateColor(state: any): string {
        if (this.colorBy === ColorBy.INSTANCE) {
            return state.color;
        }

        if (this.colorBy === ColorBy.LABEL) {
            return state.label.color;
        }

        return state.group.color;
    }

    private getDrawnObjectsWrappingBox(): WrappingBBox {
        type BoundingRect = ReturnType<PropType<fabric.Polygon, 'getBoundingRect'>>;
        type TwoCornerBox = Pick<BoundingRect, 'top' | 'left'> & { right: number; bottom: number };

        //const { width, height } = this.geometry.image;

        const width = this.geometry.image.width*this.canvasScale;
        const height = this.geometry.image.height*this.canvasScale;

        const wrappingBbox = this.drawnObjects
            .map((obj) => {
                // all objects here operate in scaled space!

                if (obj instanceof fabric.Polygon) {
                    const bbox = computeWrappingBox(obj.points
                        .reduce(((acc, val) => {
                            acc.push(val.x, val.y);
                            return acc;
                        }), []));

                    return {
                        left: bbox.xtl,
                        top: bbox.ytl,
                        width: bbox.width,
                        height: bbox.height,
                    };
                }

                return {
                    left: obj.left,
                    top: obj.top,
                    width: obj.width,
                    height: obj.height,
                };
            })
            .reduce((acc: TwoCornerBox, rect: BoundingRect) => {
                acc.top = Math.floor(Math.max(0, Math.min(rect.top, acc.top)));
                acc.left = Math.floor(Math.max(0, Math.min(rect.left, acc.left)));
                acc.bottom = Math.floor(Math.min(height, Math.max(rect.top + rect.height, acc.bottom)));
                acc.right = Math.floor(Math.min(width, Math.max(rect.left + rect.width, acc.right)));
                return acc;
            }, {
                left: Number.MAX_SAFE_INTEGER,
                top: Number.MAX_SAFE_INTEGER,
                right: Number.MIN_SAFE_INTEGER,
                bottom: Number.MIN_SAFE_INTEGER,
            });

        return wrappingBbox;
    }

    private getWrappingBoxOriginalSize(wrappingBBox: WrappingBBox): WrappingBBox {
        const {
            image: { width, height }
        } = this.geometry;

        return {
            left: Math.floor(Math.max(0, Math.min(width, wrappingBBox.left/this.canvasScale))),
            top: Math.floor(Math.max(0, Math.min(height, wrappingBBox.top/this.canvasScale))),
            right: Math.floor(Math.max(0, Math.min(width, wrappingBBox.right/this.canvasScale))),
            bottom: Math.floor(Math.max(0, Math.min(height, wrappingBBox.bottom/this.canvasScale))),
        }
    }

    // convert original mask image from upsampled canvas (and large boundingbox)
    private imageDataFromCanvas(wrappingBBox: WrappingBBox): CroppedImageData {

        const tempCanvas = document.createElement('canvas') as HTMLCanvasElement;
        const outCanvas = document.createElement('canvas') as HTMLCanvasElement;

        // Calculate the original dimensions
        const originalWidth = wrappingBBox.right - wrappingBBox.left + 0;
        const originalHeight = wrappingBBox.bottom - wrappingBBox.top + 0;

        // Calculate the new scaled dimensions
        const scaledWidth = Math.ceil(originalWidth / this.canvasScale);
        const scaledHeight = Math.ceil(originalHeight / this.canvasScale);

        tempCanvas.width = scaledWidth;
        tempCanvas.height = scaledHeight;

        const tempCtx = tempCanvas.getContext('2d');

        tempCtx.imageSmoothingEnabled = false; // Disable image smoothing to prevent blurring

        tempCtx.drawImage(
            this.canvas.toCanvasElement(),
            wrappingBBox.left, wrappingBBox.top, originalWidth, originalHeight, // Source rectangle
            0, 0, scaledWidth, scaledHeight // Destination rectangle
        );

        const tempIm = tempCtx.getImageData(0, 0, scaledWidth, scaledHeight).data;
        let bbox = this.getWrappingBoxOriginalSize(wrappingBBox);

        // console.log('---')
        // console.log(bbox)

        // crop valid region (necessary to remove erased shapes outside extents)
        let leftDelta = scaledWidth;
        let rightDelta = 0;
        let topDelta = scaledHeight;
        let bottomDelta = 0;

        for (let y=0; y<scaledHeight; y++) {
            for (let x=0; x<scaledWidth; x++) {
                const index = (y*scaledWidth + x) * 4;
                const alpha = tempIm[index + 3];

                // console.log(`${index} :: ${x}, ${y} --> ${alpha}`)

                if (alpha > 0) {
                    if (x < leftDelta) leftDelta = x;
                    if (x > rightDelta) rightDelta = x;
                    if (y < topDelta) topDelta = y;
                    if (y > bottomDelta) bottomDelta = y;
                }
            }
        }

        // account for pixel size
        rightDelta += 1;
        bottomDelta += 1;

        // console.log(bbox)
        // console.log('---')

        if (leftDelta == 0 && rightDelta == scaledWidth && topDelta == 0 && bottomDelta == scaledHeight) {
            // bbox didn't change so nothing else to do
            return { imageData: tempIm, bbox }
        }
        else {
            // adjust and redraw image using new bbox
            const outWidth = rightDelta - leftDelta;
            const outHeight = bottomDelta - topDelta;

            bbox.left += leftDelta;
            bbox.right = bbox.left + outWidth;
            bbox.top += topDelta;
            bbox.bottom = bbox.top + outHeight;

            outCanvas.width = outWidth;
            outCanvas.height = outHeight;

            const outCtx = outCanvas.getContext('2d');

            outCtx.imageSmoothingEnabled = false; // Disable image smoothing to prevent blurring

            outCtx.drawImage(
                tempCanvas,
                leftDelta, topDelta, outWidth, outHeight,
                0, 0, outWidth, outHeight // Destination rectangle
            );

            const imageData = outCtx.getImageData(0, 0, outWidth, outHeight).data;

            return { imageData, bbox }
        }
    }

    private imageDataToDataURLScaled(imageBitmap: Uint8ClampedArray, left: number, top: number, width: number, height: number, handleResult: (image: fabric.Image) => Promise<void>): void {

        // create smallCanvas that wraps incoming object (in unscaled/original image size)
        const smallCanvas = document.createElement('canvas');
        smallCanvas.width = width;
        smallCanvas.height = height;

        smallCanvas.getContext('2d').putImageData(
            new ImageData(imageBitmap, width, height), 0, 0,
        );

        // Now create the upsampled object (scaled up by canvasScale)
        const bigCanvas = document.createElement('canvas');
        bigCanvas.width = width * this.canvasScale;
        bigCanvas.height = height * this.canvasScale;

        // Draw the original image scaled-up (again, the canvas just wraps the object size, does represent ui canvas size)
        const bigCtx = bigCanvas.getContext('2d')

        bigCtx.imageSmoothingEnabled = false; // Disable image smoothing to prevent blurring

        bigCtx.drawImage(smallCanvas, 0, 0, bigCanvas.width, bigCanvas.height);

        const tempImage = new fabric.Image(bigCanvas, { left: left, top: top}); // to delete

        handleResult(tempImage);
    }

    private updateBrushTools(brushTool?: BrushTool, opts: Partial<BrushTool> = {}): void {
        if (this.isPolygonDrawing) {
            // tool was switched from polygon to brush for example
            this.keepDrawnPolygon();
        }

        this.removeBrushMarker();
        if (brushTool) {
            if (brushTool.color && this.tool?.color !== brushTool.color) {
                const color = fabric.Color.fromHex(brushTool.color);
                for (const object of this.drawnObjects) {
                    if (object instanceof fabric.Line) {
                        const alpha = +object.stroke.split(',')[3].slice(0, -1);
                        color.setAlpha(alpha);
                        object.set({ stroke: color.toRgba() });
                    } else if (
                        object instanceof fabric.Rect ||
                        object instanceof fabric.Polygon ||
                        object instanceof fabric.Circle
                    ) {
                        const alpha = +(object.fill as string).split(',')[3].slice(0, -1);
                        color.setAlpha(alpha);
                        (object as fabric.Object).set({ fill: color.toRgba() });
                    }
                }
                this.canvas.renderAll();
            }

            this.tool = { ...brushTool, ...opts };
            if (this.isDrawing || this.isEditing) {
                this.setupBrushMarker();
            }

            this.updateBlockedTools();
        }

        if (this.tool?.type?.startsWith('polygon-')) {
            this.isPolygonDrawing = true;
            this.vectorDrawHandler.draw({
                enabled: true,
                shapeType: 'polygon',
                onDrawDone: (data: { points: number[] } | null) => {
                    if (!data) return;
                    const points = data.points.reduce((acc: fabric.Point[], _: number, idx: number) => {
                        if (idx % 2) {
                            acc.push(new fabric.Point(data.points[idx - 1]*this.canvasScale, data.points[idx]*this.canvasScale));
                        }

                        return acc;
                    }, []);

                    const color = fabric.Color.fromHex(this.tool.color);
                    color.setAlpha(this.tool.type === 'polygon-minus' ? 1 : this.drawingOpacity);
                    const polygon = new fabric.Polygon(points, {
                        fill: color.toRgba(),
                        selectable: false,
                        objectCaching: false,
                        absolutePositioned: true,
                        globalCompositeOperation: this.tool.type === 'polygon-minus' ? 'destination-out' : 'xor',
                    });

                    this.canvas.add(polygon);
                    this.drawnObjects.push(polygon);
                    this.canvas.renderAll();
                },
            }, this.geometry);

            const canvasWrapper = this.canvas.getElement().parentElement as HTMLDivElement;
            canvasWrapper.style.pointerEvents = 'none';
            canvasWrapper.style.zIndex = '0';
        }
    }

    private updateBlockedTools(): void {
        if (this.drawnObjects.length === 0) {
            this.tool.onBlockUpdated({
                eraser: true,
                'polygon-minus': true,
            });
            return;
        }
        const wrappingBbox = this.getDrawnObjectsWrappingBox();
        if (this.brushMarker) {
            this.canvas.remove(this.brushMarker);
        }
        const croppedImageData = this.imageDataFromCanvas(wrappingBbox);
        if (this.brushMarker) {
            this.canvas.add(this.brushMarker);
        }
        const rle = zipChannels(croppedImageData.imageData);
        const emptyMask = rle.length < 2;
        this.tool.onBlockUpdated({
            eraser: emptyMask,
            'polygon-minus': emptyMask,
        });
    }

    private createDrawnObjectsArray(): MasksHandlerImpl['drawnObjects'] {
        const drawnObjects = [];
        const updateBlockedToolsDebounced = debounce(this.updateBlockedTools.bind(this), 250);
        return new Proxy(drawnObjects, {
            set(target, property, value) {
                target[property] = value;
                updateBlockedToolsDebounced();
                return true;
            },
        });
    }

    public constructor(
        onDrawDone: MasksHandlerImpl['onDrawDone'],
        onDrawRepeat: MasksHandlerImpl['onDrawRepeat'],
        onEditStart: MasksHandlerImpl['onEditStart'],
        onEditDone: MasksHandlerImpl['onEditDone'],
        vectorDrawHandler: DrawHandler,
        canvas: HTMLCanvasElement,
    ) {
        this.redraw = null;
        this.isDrawing = false;
        this.isEditing = false;
        this.isMouseDown = false;
        this.isBrushSizeChanging = false;
        this.isPolygonDrawing = false;
        this.drawData = null;
        this.editData = null;
        this.drawingOpacity = 0.5;
        this.brushMarker = null;
        this.colorBy = ColorBy.LABEL;
        this.onDrawDone = onDrawDone;
        this.onDrawRepeat = onDrawRepeat;
        this.onEditDone = onEditDone;
        this.onEditStart = onEditStart;
        this.vectorDrawHandler = vectorDrawHandler;

        this.canvas = new fabric.Canvas(canvas, {
            containerClass: 'cvat_masks_canvas_wrapper',
            fireRightClick: true,
            selection: false,
            defaultCursor: 'inherit',
        });

        this.canvas.imageSmoothingEnabled = false;
        this.drawnObjects = this.createDrawnObjectsArray();

        this.canvas.getElement().parentElement.addEventListener('contextmenu', (e: MouseEvent) => e.preventDefault());
        this.latestMousePos = { x: -1, y: -1 };
        window.document.addEventListener('mouseup', () => {
            this.isMouseDown = false;
            this.isBrushSizeChanging = false;
        });

        this.canvas.on('mouse:down', (options: fabric.IEvent<MouseEvent>) => {
            const { isDrawing, isEditing, isInsertion } = this;
            this.isMouseDown = (isDrawing || isEditing) && options.e.button === 0 && !options.e.altKey;
            this.isBrushSizeChanging = (isDrawing || isEditing) && options.e.button === 2 && options.e.altKey;

            if (isInsertion) {
                const continueInserting = options.e.ctrlKey;
                const wrappingBbox = this.getDrawnObjectsWrappingBox();
                const croppedImageData = this.imageDataFromCanvas(wrappingBbox);
                const rle = zipChannels(croppedImageData.imageData);
                const wrappingBboxOSize = croppedImageData.bbox;//this.getWrappingBoxOriginalSize(wrappingBbox);
                rle.push(wrappingBboxOSize.left, wrappingBboxOSize.top, wrappingBboxOSize.right, wrappingBboxOSize.bottom);

                this.onDrawDone({
                    objectType:"shape",
                    shapeType: this.drawData.shapeType,
                    points: rle,
                    label: this.drawData.initialState.label,
                }, Date.now() - this.startTimestamp, continueInserting, this.drawData);

                if (!continueInserting) {
                    this.releasePaste();
                }
            } else {
                this.canvas.fire('mouse:move', options);
            }
        });

        this.canvas.on('mouse:move', (e: fabric.IEvent<MouseEvent>) => {
            const { image: { width: imageWidth, height: imageHeight } } = this.geometry;
            const { angle } = this.geometry;

            if (e.pointer) {
                // snap to grid
                e.pointer.x = Math.floor(e.pointer.x/this.canvasScale)*this.canvasScale;
                e.pointer.y = Math.floor(e.pointer.y/this.canvasScale)*this.canvasScale;
            }

            let [x, y] = [e.pointer.x, e.pointer.y];
            if (angle === 180) {
                [x, y] = [imageWidth - x, imageHeight - y];
            } else if (angle === 270) {
                [x, y] = [imageWidth - (y / imageHeight) * imageWidth, (x / imageWidth) * imageHeight];
            } else if (angle === 90) {
                [x, y] = [(y / imageHeight) * imageWidth, imageHeight - (x / imageWidth) * imageHeight];
            }

            const position = { x, y };
            const {
                tool, isMouseDown, isInsertion, isBrushSizeChanging,
            } = this;

            if (isInsertion) {
                const [object] = this.drawnObjects;
                if (object && object instanceof fabric.Image) {
                    object.left = position.x;// - object.width / 2;
                    object.top = position.y; //- object.height / 2;
                    this.canvas.renderAll();
                }
            }

            if (isBrushSizeChanging && ['brush', 'eraser'].includes(tool?.type)) {

                const xDiff = (e.pointer.x - this.resizeBrushToolLatestX) /this.canvasScale; // divide by canvas scale so that the increment is on a per-pixel basis for a thermal image
                let onUpdateConfiguration = null;
                if (this.isDrawing) {
                    onUpdateConfiguration = this.drawData.onUpdateConfiguration;
                } else if (this.isEditing) {
                    onUpdateConfiguration = this.editData.onUpdateConfiguration;
                }
                if (onUpdateConfiguration) {
                    onUpdateConfiguration({
                        brushTool: {
                            size: Math.trunc(Math.max(1, this.tool.size + xDiff)),
                        },
                    });
                }

                this.resizeBrushToolLatestX = e.pointer.x;
                e.e.stopPropagation();
                return;
            }

            if (this.brushMarker) {
                this.brushMarker.left = position.x;
                this.brushMarker.top = position.y;
                this.canvas.bringToFront(this.brushMarker);
                this.canvas.renderAll();
            }

            if (isMouseDown && !isBrushSizeChanging && ['brush', 'eraser'].includes(tool?.type)) {
                const color = fabric.Color.fromHex(tool.color);
                color.setAlpha(tool.type === 'eraser' ? 1 : 0.5);

                const commonProperties = {
                    selectable: false,
                    evented: false,
                    globalCompositeOperation: tool.type === 'eraser' ? 'destination-out' : 'xor',
                };

                const shapeProperties = {
                    ...commonProperties,
                    fill: color.toRgba(),
                    left: Math.floor(position.x/this.canvasScale) * this.canvasScale,
                    top: Math.floor(position.y/this.canvasScale) * this.canvasScale,
                };

                let shape: fabric.Circle | fabric.Rect | null = null;
                if (tool.form === 'circle') {
                    shape = new fabric.Circle({
                        ...shapeProperties,
                        radius: tool.size * this.canvasScale / 2,
                    });
                } else if (tool.form === 'square') {
                    shape = new fabric.Rect({
                        ...shapeProperties,
                        width: tool.size * this.canvasScale,
                        height: tool.size * this.canvasScale,
                        strokeWidth: tool.type === 'eraser' ? 0: 0
                    });
                }

                this.canvas.add(shape);
                if (['brush', 'eraser'].includes(tool?.type)) {
                    this.drawnObjects.push(shape);
                }

                this.canvas.renderAll();
            } else if (tool?.type.startsWith('polygon-') && this.drawablePolygon) {
                // update the polygon position
                const points = this.drawablePolygon.get('points');
                if (points.length) {
                    points[points.length - 1].setX(e.e.offsetX);
                    points[points.length - 1].setY(e.e.offsetY);
                }
                this.canvas.renderAll();
            }

            this.latestMousePos.x = position.x;
            this.latestMousePos.y = position.y;
            this.resizeBrushToolLatestX = position.x;
        });
    }

    public updateThermalData(thermalData: any, refArray: Int16Array): void {
        this.thermalData = {
            rawData: thermalData,
            refArray: refArray
        };

        if (!thermalData) {
            this.canvasScale = 1;
        }
        else {
            this.canvasScale = 16;
        }
    }

    public configurate(configuration: Configuration): void {
        this.colorBy = configuration.colorBy;
    }

    public transform(geometry: Geometry): void {
        this.geometry = geometry;
        const {
            scale, angle, image: { width, height }, top, left, offset
        } = geometry;

        const canvasScale = this.canvasScale;

        const topCanvas = this.canvas.getElement().parentElement as HTMLDivElement;

        this.canvas.setHeight(height*canvasScale);
        this.canvas.setWidth(width*canvasScale);
        this.canvas.setDimensions({ width: width*canvasScale, height: height*canvasScale });

        topCanvas.style.transformOrigin = 'top left'
        topCanvas.style.top = `${0}px`;
        topCanvas.style.left =`${0}px`;
        topCanvas.style.transform = `translate(${left+width/2}px, ${top+height/2}px) scale(${scale/canvasScale}) rotate(${angle}deg) translate(${-width*canvasScale/2}px, ${-height*canvasScale/2}px)`;

        if (this.drawablePolygon) {
            this.drawablePolygon.set('strokeWidth', consts.BASE_STROKE_WIDTH / scale);
            this.canvas.renderAll();
        }
    }

    public draw(drawData: DrawData): void {
        if (drawData.oneshotTool) {
            // apply oneshot segmentation

            const color = fabric.Color.fromHex(drawData.brushTool.color).getSource();
            const raw = this.thermalData.rawData.raw;
            const ref = this.thermalData.refArray;
            const tf = this.thermalData.rawData.raw_md.tf;
            const rawWidth = this.thermalData.rawData.raw_md.c;
            const rawHeight = this.thermalData.rawData.raw_md.r;
            const maskWidth = this.thermalData.rawData.raw_md.vc;
            const maskHeight = this.thermalData.rawData.raw_md.vr;
            const xOffset = this.thermalData.rawData.raw_md.coff;
            const yOffset = this.thermalData.rawData.raw_md.roff;
            const doOneshot = drawData.oneshotTool.enabled;
            const threshold = drawData.oneshotTool.threshold;

            // if oneshot is disabled, wipe the mask and return
            if (!doOneshot && this.oneshotMask) {
                this.canvas.remove(this.oneshotMask);
                this.oneshotMask = null;
                return;
            }

            // otherwise compute the mask
            let mask = new Uint8ClampedArray(4*maskWidth*maskHeight).fill(0);
            let isEmptyMask = true;

            // index offset of mask relative to raw
            // const rawIndexOffset = yOffset*rawWidth + xOffset;

            for (let i=0; i<(maskWidth*maskHeight); i++) {
                const y_raw = (Math.floor(i/maskWidth)) + yOffset;
                const x_raw = (i%maskWidth) + xOffset;
                const i_raw = y_raw*rawWidth + x_raw;
                // console.log(`${x_raw}, ${y_raw} :: ${i_raw} <-- ${i}`)

                if ( (raw[i_raw]-ref[i_raw])/tf > threshold) {
                    mask[4*i+0] = color[0];
                    mask[4*i+1] = color[1];
                    mask[4*i+2] = color[2];
                    mask[4*i+3] = 255;
                    isEmptyMask = false;
                }
            }

            if (!isEmptyMask) {
                // replace oneshot mask addition
                this.imageDataToDataURLScaled(mask, 0, 0, maskWidth, maskHeight,
                    (image: fabric.Image) => new Promise((resolve) => {
                        try {
                            image.selectable = false;
                            image.evented = false;
                            image.globalCompositeOperation = 'xor';
                            image.opacity = 0.5;

                            if (this.oneshotMask) {
                                this.canvas.remove(this.oneshotMask);
                            }

                            this.oneshotMask = image; // oneshot layer is reusable
                            this.canvas.add(image);
                            this.drawnObjects.push(image);
                            this.canvas.renderAll();
                        } finally {
                            resolve();
                        }
                    }));
            }
            else {
                // oneshot mask resulted in empty mask, so clear
                if (this.oneshotMask) {
                    this.canvas.remove(this.oneshotMask);
                    this.oneshotMask = null;
                }
            }

            return;
        }

        if (drawData.enabled && drawData.shapeType === 'mask') {
            if (!this.isInsertion && drawData.initialState?.shapeType === 'mask') {
                // initialize inserting pipeline if not started
                const { points } = drawData.initialState;
                const color = fabric.Color.fromHex(this.getStateColor(drawData.initialState)).getSource();
                const [left0, top0, right0, bottom0] = points.slice(-4);
                const left = left0*this.canvasScale;
                const top = top0*this.canvasScale;
                const imageBitmap = expandChannels(color[0], color[1], color[2], points);

                this.imageDataToDataURLScaled(imageBitmap, left, top, right0 - left0 + 0, bottom0 - top0 + 0,
                    (image: fabric.Image) => new Promise((resolve) => {
                        try {
                            image.selectable = false;
                            image.evented = false;
                            image.globalCompositeOperation = 'xor';
                            image.opacity = 0.5;
                            this.canvas.add(image);
                            this.drawnObjects.push(image);
                            this.canvas.renderAll();
                        } finally {
                            resolve();
                        }
                    }));

                this.isInsertion = true;
            } else {
                this.updateBrushTools(drawData.brushTool);
                if (!this.isDrawing) {
                    // initialize drawing pipeline if not started
                    this.isDrawing = true;
                    this.redraw = drawData.redraw || null;
                }
            }

            this.canvas.getElement().parentElement.style.display = 'block';
            this.startTimestamp = Date.now();
        }

        if (!drawData.enabled && this.isDrawing) {
            try {
                if (this.drawnObjects.length) {
                    const wrappingBbox = this.getDrawnObjectsWrappingBox();
                    this.removeBrushMarker(); // remove brush marker from final mask
                    const croppedImageData = this.imageDataFromCanvas(wrappingBbox); // get mask in original image size from upsampled canvas
                    const rle = zipChannels(croppedImageData.imageData); // rle "small" image
                    const wrappingBboxOSize = croppedImageData.bbox; //this.getWrappingBoxOriginalSize(wrappingBbox); // get wrapping box "small" size
                    // console.log(wrappingBboxOSize)
                    rle.push(wrappingBboxOSize.left, wrappingBboxOSize.top, wrappingBboxOSize.right, wrappingBboxOSize.bottom);

                    // console.log(`rle post: ${rle}`) // should be "small" rle with "small" bb
                    const isEmptyMask = rle.length < 6;

                    if (isEmptyMask) {
                        this.onDrawDone(null);
                    } else {
                        this.onDrawDone({
                            objectType:"shape",
                            shapeType: this.drawData.shapeType,
                            points: rle,
                            ...(Number.isInteger(this.redraw) ? { clientID: this.redraw } : {}),
                        }, Date.now() - this.startTimestamp, drawData.continue, this.drawData);
                    }
                } else {
                    this.onDrawDone(null);
                }
            } finally {
                this.releaseDraw();
            }

            if (drawData.continue) {
                const newDrawData = {
                    ...this.drawData,
                    brushTool: { ...this.tool },
                    ...drawData,
                    enabled: true,
                    shapeType: 'mask',
                };

                this.onDrawRepeat({ enabled: true, shapeType: 'mask' });
                this.onDrawRepeat(newDrawData);
                return;
            }
        }

        this.drawData = drawData;
    }

    public edit(editData: MasksEditData): void {
        if (editData.enabled && editData.state.shapeType === 'mask') {
            if (!this.isEditing) {
                // start editing pipeline if not started yet
                this.canvas.getElement().parentElement.style.display = 'block';
                const { points } = editData.state;
                const color = fabric.Color.fromHex(this.getStateColor(editData.state)).getSource();
                const [left0, top0, right0, bottom0] = points.slice(-4);
                const left = left0*this.canvasScale;
                const top = top0*this.canvasScale;
                const imageBitmap = expandChannels(color[0], color[1], color[2], points);

                this.imageDataToDataURLScaled(imageBitmap, left, top, right0 - left0 + 0, bottom0 - top0 + 0,
                    (image: fabric.Image) => new Promise((resolve) => {
                        try {
                            image.selectable = false;
                            image.evented = false;
                            image.globalCompositeOperation = 'xor';
                            image.opacity = 0.5;
                            this.canvas.add(image);
                            this.drawnObjects.push(image);
                            this.canvas.renderAll();
                        } finally {
                            resolve();
                        }
                    }));

                this.isEditing = true;
                this.startTimestamp = Date.now();
                this.onEditStart(editData.state);
            }
        }

        this.updateBrushTools(
            editData.brushTool,
            editData.state ? { color: this.getStateColor(editData.state) } : {},
        );

        if (!editData.enabled && this.isEditing) {
            try {
                if (this.drawnObjects.length) {
                    const wrappingBbox = this.getDrawnObjectsWrappingBox();
                    this.removeBrushMarker(); // remove brush marker from final mask
                    const croppedImageData = this.imageDataFromCanvas(wrappingBbox);
                    const rle = zipChannels(croppedImageData.imageData);
                    const wrappingBboxOSize = croppedImageData.bbox;//this.getWrappingBoxOriginalSize(wrappingBbox);
                    rle.push(wrappingBboxOSize.left, wrappingBboxOSize.top, wrappingBboxOSize.right, wrappingBboxOSize.bottom);

                    const isEmptyMask = rle.length < 6;
                    if (isEmptyMask) {
                        this.onEditDone(null, null);
                    } else {
                        this.onEditDone(this.editData.state, rle);
                    }
                }
            } finally {
                this.releaseEdit();
            }
        }
        this.editData = editData;
    }

    get enabled(): boolean {
        return this.isDrawing || this.isEditing || this.isInsertion;
    }

    public cancel(): void {
        if (this.isDrawing || this.isInsertion) {
            this.releaseDraw();
        }

        if (this.isEditing) {
            this.releaseEdit();
        }
    }
}
